import { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import {
  IBuilder,
  builderRequestError,
  useBuilders,
} from "../../hooks/api/admin/useBuilders";
import {
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";
import { COMPONENTS, SPACING } from "../../constants/design/spacing";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import ConfirmationModal from "../shared/ConfirmationModal";
import { BaseModal } from "../shared/BaseModal";
import {
  PaginationButton,
  PaginationControls,
  PaginationRow,
  PaginationSelect,
} from "../Ondo/style";
import { PartnerLinksModal } from "./PartnerLinks";
import { BuilderDetailModal } from "./BuilderDetail";
import { OptionsFeePanel } from "./OptionsFeePanel";
import {
  CopyText,
  StatusBadge,
  StatusConfirmModals,
  cellStyle,
  errorStyle,
  headerCellStyle,
  inputStyle,
  labelStyle,
  panelStyle,
} from "./shared";
import {
  BUILDER_CREATION_STATUSES,
  BUILDER_STATUSES,
  BuilderSortKey,
  BuilderStatus,
  BuilderStatusFilter,
  allowedStatusTransitions,
  feeRateToBps,
  formatFeeRate,
  validateBuilderName,
  validateChecksumAddress,
  validateFeeRate,
  validateMinCreateBalance,
} from "../../utils/builders";

// ---------------------------------------------------------------------------
// Protocol cap panel
// ---------------------------------------------------------------------------

interface ICapPanelProps {
  cap: string | null | undefined;
  isLoading: boolean;
  disabled: boolean;
  onSave: (rate: string) => Promise<boolean>;
}

function ProtocolCapPanel({
  cap, isLoading, disabled, onSave,
}: ICapPanelProps) {
  const [input, setInput] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputError = input ? validateFeeRate(input) : undefined;
  const isUnset = !isLoading && !disabled && (cap === null || cap === undefined);

  const onConfirm = useCallback(async () => {
    setSubmitting(true);
    const ok = await onSave(input);
    setSubmitting(false);
    if (ok) {
      setConfirming(false);
      setInput("");
    }
  }, [input, onSave]);

  let capDisplay: JSX.Element;
  if (isLoading) {
    capDisplay = <span style={{ color: TEXT_COLORS.two }}>Loading…</span>;
  } else if (disabled) {
    capDisplay = <span style={{ color: TEXT_COLORS.two }}>Unavailable</span>;
  } else if (isUnset) {
    capDisplay = (
      <span style={{ color: COLORS.red.one, fontWeight: 700 }}>
        NOT SET — builder fees disabled
      </span>
    );
  } else {
    capDisplay = (
      <span style={{ color: TEXT_COLORS.one, fontWeight: 600 }}>
        {formatFeeRate(cap as string)}{" "}
        <span style={{ color: TEXT_COLORS.three, fontFamily: "monospace" }}>
          ({cap})
        </span>
      </span>
    );
  }

  return (
    <div
      style={{
        ...panelStyle,
        flex: 1,
        minWidth: 360,
        background: isUnset ? COLORS.red.five : panelStyle.background,
      }}
    >
      <div style={{ ...FONT_STYLE.header.six, marginBottom: SPACING.two }}>
        Protocol cap: max builder fee rate (perps)
      </div>
      <div style={{ marginBottom: SPACING.two }}>{capDisplay}</div>
      {isUnset && (
        <div style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.two, marginBottom: SPACING.two }}>
          While the cap is unset, all builder approvals and builder orders are rejected.
        </div>
      )}
      <div style={{ display: "flex", gap: SPACING.two, alignItems: "flex-start" }}>
        <div style={{ width: 240 }}>
          <input
            name="max_fee_rate_perps"
            placeholder="New cap, e.g. 0.001 (= 10 bps)"
            value={input}
            onChange={(e) => setInput(e.target.value.trim())}
            style={inputStyle}
            disabled={disabled}
            aria-label="New max builder fee rate"
          />
          {inputError && <div style={errorStyle}>{inputError}</div>}
          {input && !inputError && (
            <div style={{ ...FONT_STYLE.label.three, color: TEXT_COLORS.two, marginTop: SPACING.one }}>
              = {formatFeeRate(input)}
            </div>
          )}
        </div>
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          disabled={!input || !!inputError || isLoading || disabled}
          onClick={() => setConfirming(true)}
          style={{ height: 32, padding: `0 ${SPACING.two}px` }}
        >
          Set cap
        </Button>
      </div>
      <ConfirmationModal
        show={confirming}
        onHide={() => setConfirming(false)}
        title="Confirm protocol builder fee cap"
        primaryColor={COLORS.orange.one}
        width="420px"
        minWidth="420px"
        isLoading={submitting}
        stats={[
          {
            title: "Current",
            data: cap ? formatFeeRate(cap) : "NOT SET",
          },
          {
            title: "New",
            data: input ? `${formatFeeRate(input)}` : "-",
          },
          { title: "New (bps)", data: input ? `${feeRateToBps(input)} bps` : "-" },
        ]}
        warning="Builder orders whose fee exceeds the new cap will be rejected."
        confirmationButton={{
          title: "Set cap",
          onClick: onConfirm,
          disabled: !input || !!inputError,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Self-service minimum balance panel
// ---------------------------------------------------------------------------

interface IMinBalancePanelProps {
  minCreateBalance: string | null | undefined;
  isLoading: boolean;
  disabled: boolean;
  onSave: (balance: string) => Promise<boolean>;
}

function MinCreateBalancePanel({
  minCreateBalance, isLoading, disabled, onSave,
}: IMinBalancePanelProps) {
  const [input, setInput] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputError = input ? validateMinCreateBalance(input) : undefined;
  const isUnset = !isLoading && !disabled
    && (minCreateBalance === null || minCreateBalance === undefined);

  const onConfirm = useCallback(async () => {
    setSubmitting(true);
    const ok = await onSave(input);
    setSubmitting(false);
    if (ok) {
      setConfirming(false);
      setInput("");
    }
  }, [input, onSave]);

  let display: JSX.Element;
  if (isLoading) {
    display = <span style={{ color: TEXT_COLORS.two }}>Loading…</span>;
  } else if (disabled) {
    display = <span style={{ color: TEXT_COLORS.two }}>Unavailable</span>;
  } else if (isUnset) {
    display = (
      <span style={{ color: COLORS.red.one, fontWeight: 700 }}>
        NOT SET — self-service registration disabled
      </span>
    );
  } else {
    display = (
      <span style={{ color: TEXT_COLORS.one, fontWeight: 600 }}>
        {`${minCreateBalance} USDC`}
      </span>
    );
  }

  return (
    <div
      style={{
        ...panelStyle,
        flex: 1,
        minWidth: 360,
        background: isUnset ? COLORS.red.five : panelStyle.background,
      }}
    >
      <div style={{ ...FONT_STYLE.header.six, marginBottom: SPACING.two }}>
        Self-service minimum balance
      </div>
      <div style={{ marginBottom: SPACING.two }}>{display}</div>
      <div style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.two, marginBottom: SPACING.two }}>
        Minimum USDC balance an account needs to register its own builder.
        {isUnset && " While unset, self-service registration is rejected."}
      </div>
      <div style={{ display: "flex", gap: SPACING.two, alignItems: "flex-start" }}>
        <div style={{ width: 240 }}>
          <input
            name="min_create_balance"
            placeholder="New minimum, e.g. 100 (USDC)"
            value={input}
            onChange={(e) => setInput(e.target.value.trim())}
            style={inputStyle}
            disabled={disabled}
            aria-label="New self-service minimum balance"
          />
          {inputError && <div style={errorStyle}>{inputError}</div>}
        </div>
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          disabled={!input || !!inputError || isLoading || disabled}
          onClick={() => setConfirming(true)}
          style={{ height: 32, padding: `0 ${SPACING.two}px` }}
        >
          Set minimum
        </Button>
      </div>
      <ConfirmationModal
        show={confirming}
        onHide={() => setConfirming(false)}
        title="Confirm self-service minimum balance"
        primaryColor={COLORS.orange.one}
        width="420px"
        minWidth="420px"
        isLoading={submitting}
        stats={[
          {
            title: "Current",
            data: minCreateBalance ? `${minCreateBalance} USDC` : "NOT SET",
          },
          { title: "New", data: input ? `${input} USDC` : "-" },
        ]}
        warning="Accounts with a USDC balance below the new minimum can no longer register a builder. The protocol fee cap is unchanged."
        confirmationButton={{
          title: "Set minimum",
          onClick: onConfirm,
          disabled: !input || !!inputError,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / edit form
// ---------------------------------------------------------------------------

interface IBuilderFormProps {
  // undefined -> create mode
  builder?: IBuilder;
  show: boolean;
  onHide: () => void;
  onSubmit: (values: IBuilderFormValues) => Promise<boolean>;
}

interface IBuilderFormValues {
  name: string;
  fee_account: string;
  status: BuilderStatus;
}

const DEFAULT_PER_PAGE = 25;
const PER_PAGE_OPTIONS = [10, 25, 50];
const SEARCH_DEBOUNCE_MS = 300;
const modalScrollStyle: CSSProperties = {
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
};

function BuilderFormModal({ builder, show, onHide, onSubmit }: IBuilderFormProps) {
  const isCreate = !builder;
  const [name, setName] = useState(builder?.name ?? "");
  const [feeAccount, setFeeAccount] = useState(builder?.fee_account ?? "");
  const [status, setStatus] = useState<BuilderStatus>(builder?.status ?? "ACTIVE");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const [confirmStatus, setConfirmStatus] = useState<BuilderStatus>();

  const nameError = validateBuilderName(name);
  const feeAccountError = validateChecksumAddress(feeAccount);
  const hasErrors = !!nameError || !!feeAccountError;

  const statusOptions = isCreate
    ? BUILDER_CREATION_STATUSES
    : allowedStatusTransitions(builder.status);

  const submit = useCallback(async () => {
    setSubmitting(true);
    setServerError(undefined);
    const ok = await onSubmit({ name, fee_account: feeAccount, status });
    setSubmitting(false);
    setConfirmStatus(undefined);
    if (ok) {
      onHide();
    } else {
      setServerError("Request rejected — see the error toast for details.");
    }
  }, [feeAccount, name, onHide, onSubmit, status]);

  const onSave = useCallback(() => {
    setTouched(true);
    if (hasErrors) return;
    const statusChanged = isCreate ? status !== "ACTIVE" : status !== builder.status;
    if (statusChanged && (status === "SUSPENDED" || status === "DISABLED")) {
      setConfirmStatus(status);
      return;
    }
    submit();
  }, [builder, hasErrors, isCreate, status, submit]);

  return (
    <>
      <BaseModal
        show={show && !confirmStatus}
        onHide={onHide}
        title={isCreate ? "Create builder" : `Edit builder ${builder.builder_id}`}
        style={{ ...modalScrollStyle, width: 480, minWidth: 480 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: SPACING.three }}>
          <div>
            <label htmlFor="builder-name" style={labelStyle}>
              Name
              <input
                id="builder-name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
                maxLength={64}
              />
            </label>
            {touched && nameError && <div style={errorStyle}>{nameError}</div>}
          </div>
          <div>
            <label htmlFor="builder-fee-account" style={labelStyle}>
              Fee account (EIP-55 checksum address)
              <input
                id="builder-fee-account"
                name="fee_account"
                value={feeAccount}
                onChange={(e) => setFeeAccount(e.target.value.trim())}
                style={{ ...inputStyle, fontFamily: "monospace" }}
                placeholder="0x…"
              />
            </label>
            {(touched || feeAccount) && feeAccountError && (
              <div style={errorStyle}>{feeAccountError}</div>
            )}
          </div>
          <div>
            <label htmlFor="builder-status" style={labelStyle}>
              Status
              <select
                id="builder-status"
                name="status"
                value={status}
                onChange={(e) => setStatus(e.target.value as BuilderStatus)}
                style={inputStyle}
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
              ))}
              </select>
            </label>
          </div>
          {serverError && <div style={errorStyle}>{serverError}</div>}
          <Button
            fullWidth
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={submitting || (touched && hasErrors)}
            onClick={onSave}
          >
            {isCreate ? "Create builder" : "Save changes"}
          </Button>
        </div>
      </BaseModal>
      <StatusConfirmModals
        show={show}
        target={confirmStatus}
        builderName={name}
        submitting={submitting}
        onHide={() => setConfirmStatus(undefined)}
        onConfirm={submit}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function Builders() {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<IBuilder>();
  const [sharing, setSharing] = useState<{ builder: IBuilder; justCreated: boolean }>();
  const [detailBuilder, setDetailBuilder] = useState<IBuilder>();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<BuilderStatusFilter>("ALL");
  const [sortKey, setSortKey] = useState<BuilderSortKey>("name");
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  useEffect(() => {
    const nextSearch = search.trim();
    if (nextSearch === debouncedSearch) {
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(nextSearch);
      setCurrentPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [debouncedSearch, search]);

  const {
    data, error, isLoading, isStale, mutate, setBuilder, setMaxFeeRatePerps, setMinCreateBalance,
    setMaxFeeRateOptions, setOptionPremiumCap,
  } = useBuilders({
    limit: perPage,
    offset: currentPage * perPage,
    search: debouncedSearch,
    status: statusFilter,
    sort: sortKey,
  });

  const builders = useMemo(() => data?.builders ?? [], [data]);
  const totalBuilders = useMemo(() => {
    const parsed = Number(data?.count);
    return Number.isFinite(parsed) ? parsed : builders.length;
  }, [builders.length, data?.count]);
  const pageCount = Math.max(1, Math.ceil(totalBuilders / perPage));
  const rangeStart = totalBuilders === 0 ? 0 : currentPage * perPage + 1;
  const rangeEnd = totalBuilders === 0
    ? 0
    : Math.min(totalBuilders, (currentPage + 1) * perPage);
  const hasListFilters = Boolean(debouncedSearch) || statusFilter !== "ALL";
  const resolvedDetailBuilder = detailBuilder
    ? builders.find((builder) => builder.builder_id === detailBuilder.builder_id) ?? detailBuilder
    : undefined;

  useEffect(() => {
    const lastPage = Math.max(0, pageCount - 1);
    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [currentPage, pageCount]);

  const onSaveMinBalance = useCallback(
    async (balance: string) => (await setMinCreateBalance(balance)).ok,
    [setMinCreateBalance]
  );

  const onSaveOptionsRate = useCallback(
    async (rate: string) => (await setMaxFeeRateOptions(rate)).ok,
    [setMaxFeeRateOptions]
  );

  const onSaveOptionPremiumCap = useCallback(
    async (cap: string) => (await setOptionPremiumCap(cap)).ok,
    [setOptionPremiumCap]
  );

  const onSaveCap = useCallback(
    async (rate: string) => (await setMaxFeeRatePerps(rate)).ok,
    [setMaxFeeRatePerps]
  );

  const onStatusFilterChange = useCallback((value: BuilderStatusFilter) => {
    setStatusFilter(value);
    setCurrentPage(0);
  }, []);

  const onSortKeyChange = useCallback((value: BuilderSortKey) => {
    setSortKey(value);
    setCurrentPage(0);
  }, []);

  const onPerPageChange = useCallback((value: number) => {
    setPerPage(value);
    setCurrentPage(0);
  }, []);

  const loadError = error ? builderRequestError(error) ?? "Please try again." : undefined;
  // Fail closed: no edits unless the authoritative list loaded.
  const editsDisabled = !data || Boolean(error);
  const rowActionsDisabled = editsDisabled || isStale;
  let emptyBuildersMessage: string | undefined;
  if (isLoading && !data) {
    emptyBuildersMessage = "Loading…";
  } else if (hasListFilters) {
    emptyBuildersMessage = "No builders match the filters";
  } else if (totalBuilders > 0) {
    emptyBuildersMessage = "No builders on this page";
  } else {
    emptyBuildersMessage = "No builders yet";
  }

  return (
    <div
      style={{
        height: `calc(100vh - ${COMPONENTS.header.main}px)`,
        padding: SPACING.three,
        boxSizing: "border-box",
        color: TEXT_COLORS.one,
        overflowY: "auto",
      }}
    >
      <div style={{ ...FONT_STYLE.header.four, marginBottom: SPACING.three }}>
        Builder Codes
      </div>

      {loadError && (
        <div role="alert" style={{ ...panelStyle, color: COLORS.red.one }}>
          Unable to load builders: {loadError}{" "}
          <Button type="button" onClick={() => mutate()} buttonTheme={ButtonThemeEnum.NEUTRAL2}>
            Retry
          </Button>
        </div>
      )}

      <div style={{ display: "flex", gap: SPACING.three, flexWrap: "wrap" }}>
        <ProtocolCapPanel
          cap={data?.max_fee_rate_perps}
          isLoading={isLoading || (!data && !error)}
          disabled={editsDisabled}
          onSave={onSaveCap}
        />
        <MinCreateBalancePanel
          minCreateBalance={data?.min_create_balance}
          isLoading={isLoading || (!data && !error)}
          disabled={editsDisabled}
          onSave={onSaveMinBalance}
        />
        <OptionsFeePanel
          maxFeeRateOptions={data?.max_fee_rate_options}
          optionPremiumCap={data?.option_premium_cap}
          isLoading={isLoading || (!data && !error)}
          disabled={editsDisabled}
          onSaveRate={onSaveOptionsRate}
          onSavePremiumCap={onSaveOptionPremiumCap}
        />
      </div>

      <div style={panelStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: SPACING.two,
          }}
        >
          <div style={FONT_STYLE.header.six}>
            {`Builders (${totalBuilders})`}
          </div>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={editsDisabled}
            onClick={() => setCreating(true)}
            style={{ height: 32, padding: `0 ${SPACING.two}px` }}
          >
            + Create builder
          </Button>
        </div>
        <div style={{ display: "flex", gap: SPACING.two, marginBottom: SPACING.two, flexWrap: "wrap" }}>
          <input
            type="search"
            placeholder="Search id, name or fee account"
            aria-label="Search builders"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...inputStyle, marginTop: 0, width: 320 }}
          />
          <select
            aria-label="Status filter"
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as BuilderStatusFilter)}
            style={{ ...inputStyle, marginTop: 0, width: 160 }}
          >
            <option value="ALL">All statuses</option>
            {BUILDER_STATUSES.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <select
            aria-label="Sort by"
            value={sortKey}
            onChange={(e) => onSortKeyChange(e.target.value as BuilderSortKey)}
            style={{ ...inputStyle, marginTop: 0, width: 160 }}
          >
            <option value="name">Sort by name</option>
            <option value="status">Sort by status</option>
          </select>
        </div>
        <PaginationControls style={{ marginBottom: SPACING.two }}>
          <span style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.two }}>
            {rangeStart}-{rangeEnd} of {totalBuilders}
          </span>
          <PaginationSelect
            aria-label="Builders per page"
            value={perPage}
            disabled={isStale}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
          >
            {PER_PAGE_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value} / page
              </option>
            ))}
          </PaginationSelect>
          <PaginationRow>
            <PaginationButton
              type="button"
              disabled={isStale || currentPage <= 0}
              onClick={() => setCurrentPage((page) => Math.max(0, page - 1))}
            >
              Prev
            </PaginationButton>
            <span style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.two }}>
              Page {currentPage + 1} / {pageCount}
            </span>
            <PaginationButton
              type="button"
              disabled={isStale || currentPage >= pageCount - 1}
              onClick={() =>
                setCurrentPage((page) => Math.min(pageCount - 1, page + 1))}
            >
              Next
            </PaginationButton>
          </PaginationRow>
        </PaginationControls>
        <div
          aria-busy={isStale}
          style={{
            overflowX: "auto",
            opacity: isStale ? 0.55 : 1,
            transition: "opacity 120ms ease",
          }}
        >
          <table style={{ width: "100%", minWidth: 920, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Name</th>
                <th style={headerCellStyle}>Builder ID</th>
                <th style={headerCellStyle}>Fee account</th>
                <th style={headerCellStyle}>Status</th>
                <th style={headerCellStyle}>{" "}</th>
              </tr>
            </thead>
            <tbody>
              {builders.map((builder) => (
                <tr key={builder.builder_id}>
                  <td style={cellStyle}>{builder.name}</td>
                  <td style={cellStyle}>
                    <CopyText text={builder.builder_id} />
                  </td>
                  <td style={cellStyle}>
                    <CopyText text={builder.fee_account} />
                  </td>
                  <td style={cellStyle}>
                    <StatusBadge status={builder.status} />
                  </td>
                  <td style={{ ...cellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                    <div
                      data-testid={`builder-actions-${builder.builder_id}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: SPACING.one,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Button
                        buttonTheme={ButtonThemeEnum.NEUTRAL3}
                        disabled={rowActionsDisabled}
                        aria-label={`Details for ${builder.name}`}
                        onClick={() => setDetailBuilder(builder)}
                        style={{ height: 28, padding: `0 ${SPACING.two}px` }}
                      >
                        Details
                      </Button>
                      <Button
                        buttonTheme={ButtonThemeEnum.NEUTRAL3}
                        disabled={rowActionsDisabled}
                        aria-label={`Partner links for ${builder.name}`}
                        onClick={() => setSharing({ builder, justCreated: false })}
                        style={{ height: 28, padding: `0 ${SPACING.two}px` }}
                      >
                        Partner links
                      </Button>
                      <Button
                        buttonTheme={ButtonThemeEnum.NEUTRAL3}
                        disabled={rowActionsDisabled}
                        onClick={() => setEditing(builder)}
                        style={{ height: 28, padding: `0 ${SPACING.two}px` }}
                      >
                        Edit
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!builders.length && (
                <tr>
                  <td style={{ ...cellStyle, color: TEXT_COLORS.two }} colSpan={5}>
                    {emptyBuildersMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <BuilderFormModal
          show={creating}
          onHide={() => setCreating(false)}
          onSubmit={async (values) => {
            const result = await setBuilder(values);
            if (result.ok && result.builderId) {
              setSharing({
                builder: { builder_id: result.builderId, ...values },
                justCreated: true,
              });
            }
            return result.ok;
          }}
        />
      )}
      {editing && (
        <BuilderFormModal
          key={editing.builder_id}
          builder={editing}
          show={!!editing}
          onHide={() => setEditing(undefined)}
          onSubmit={async (values) =>
            (await setBuilder({ builder_id: editing.builder_id, ...values })).ok}
        />
      )}
      {resolvedDetailBuilder && (
        <BuilderDetailModal
          key={resolvedDetailBuilder.builder_id}
          builder={resolvedDetailBuilder}
          show={!!resolvedDetailBuilder}
          onHide={() => setDetailBuilder(undefined)}
          actionsDisabled={editsDisabled}
          onSetStatus={async (status) => {
            const result = await setBuilder({
              builder_id: resolvedDetailBuilder.builder_id,
              name: resolvedDetailBuilder.name,
              fee_account: resolvedDetailBuilder.fee_account,
              status,
            });
            if (result.ok) {
              setDetailBuilder({ ...resolvedDetailBuilder, status });
            }
            return result.ok;
          }}
        />
      )}
      {sharing && (
        <PartnerLinksModal
          key={sharing.builder.builder_id}
          builder={sharing.builder}
          cap={data?.max_fee_rate_perps}
          show={!!sharing}
          onHide={() => setSharing(undefined)}
          title={sharing.justCreated ? `Builder created — partner links for ${sharing.builder.name}` : undefined}
        />
      )}
    </div>
  );
}
